import {
  UseBaseQueryOptions,
  useQuery,
  useMutation,
  useQueryClient,
  InfiniteData,
} from "@tanstack/react-query";
import { AxiosError, isAxiosError } from "axios";
import { produce } from "immer";
import { capitalize, chunk } from "lodash-es";

import { useUploadFile } from "src/api/filesApi/queries";
import { deserializeJsonApi } from "src/api/jsonApiHelpers";
import { useURLParamsFilters } from "src/base-components/tableColumnFilters/useFilters";
import { CaseActionFormValues } from "src/cases/caseView/schema";
import {
  CaseDeserialized,
  CasesDeserialized,
  TaskIdExtractor,
} from "src/cases/queries/casesTypes";
import { useCasesClient } from "src/cases/queries/client";
import { caseQueuesKeys } from "src/cases/queries/queues";
import { CaseQueueSummary } from "src/cases/queues/types";
import {
  CaseResponse,
  CasePatchRequest,
  CasePatchResponse,
  CaseBulkAtomicOperationsRequestAtomicOperationsInner,
  CasesResponse,
  RelatedEntitiesBlockConfigPatch,
  RelatedEntitiesBlockConfigPatchResponse,
  ModelsCaseDataApiModelsRelatedEntitiesBlockConfigPatchDataTypeEnum,
  FileCategoriesAtomicOperationsRequest,
  FileCategoriesAtomicResultsResponse,
  FileCategoriesAtomicOperation,
  FileCategoriesAtomicOperationOpEnum,
  RelatedEventsBlockConfigPatch,
  RelatedEventsBlockConfigPatchResponse,
  FileCategoriesAtomicResourceObjectTypeEnum,
  GetCasesApiV1CasesGetFilterEnvEnum,
  RelatedEventsCaseBlockConfigPatchAttributes,
  RelatedEntitiesCaseBlockConfigPatchAttributes,
  CaseActionFormPatchResponse,
  CaseTaskCompletionPatchResponse,
  CaseTaskFormPatchResponse,
  RunningActionsResponse,
  AttributesValue,
} from "src/clients/case-management";
import { UploadFileEnvFilesPostEnvEnum } from "src/clients/files";
import { toastActions } from "src/design-system/Toast/utils";
import { useEnvironment } from "src/hooks/useEnvironment";
import { DashboardPageParams } from "src/router/urls";
import { useCurrentUserId } from "src/store/AuthStore";
import { FileSource } from "src/utils/files";
import { assertUnreachable } from "src/utils/typeUtils";
import { encodeUrlParams } from "src/utils/url";
import { useParamsDecode } from "src/utils/useParamsDecode";

// Constants
const STALE_TIME = 1000 * 60 * 5; // 5 minutes

// HTTP status codes that should not trigger a retry
const NO_RETRY_STATUS_CODES = [401, 403, 404, 409] as const;

// Query key factory for cache management
export const casesKeys = {
  all: (workspaceId: string) => ["cases", workspaceId] as const,
  get: (workspaceId: string, caseId: string) =>
    [...casesKeys.all(workspaceId), "case", caseId] as const,
  list: (
    workspaceId: string,
    environment: string,
    filters?: Record<string, string>,
  ) => [...casesKeys.all(workspaceId), "list", environment, filters] as const,
};

// Type definitions
type CaseQueryOptions<TData = CaseResponse> = UseBaseQueryOptions<
  CaseResponse,
  AxiosError,
  TData
>;

const useCasesWorkspaceParams = () => {
  const { wsId } = useParamsDecode<DashboardPageParams>();
  const apiClient = useCasesClient();

  return { wsId, apiClient };
};

const getSerializedData = (data: CaseResponse) => {
  return deserializeJsonApi<CaseDeserialized>(data);
};

/**
 * Increments etag value handling both string and number types from API
 * Remove this helper when backend consistently sends etags as single type
 */
const incrementEtag = (currentEtag: string | number): string => {
  const etagString = String(currentEtag);
  return (parseInt(etagString, 10) + 1).toString();
};

// Main hooks
export const useCaseRaw = <TData = CaseResponse>(
  caseId?: string | null,
  options?: Omit<CaseQueryOptions<TData>, "queryKey" | "queryFn">,
) => {
  const { wsId, apiClient } = useCasesWorkspaceParams();
  const queryKey = casesKeys.get(wsId, caseId || "");

  return useQuery<CaseResponse, AxiosError, TData>({
    queryKey,
    queryFn: async (context) => {
      if (!apiClient || !caseId) {
        throw new Error("Missing required parameters");
      }

      const cachedCase = context.client.getQueryData<CaseResponse>(queryKey);

      try {
        const response = await apiClient.getCaseApiV1CasesIdGet({
          id: caseId,
        });

        return response.data;
      } catch (error) {
        // Axios treats 304 as an error
        if (
          isAxiosError(error) &&
          error.response?.status === 304 &&
          cachedCase
        ) {
          return cachedCase;
        }

        throw error;
      }
    },
    retry: (failureCount, error) => {
      // Don't retry on certain HTTP status codes (auth errors, not found, outdated, etc.)
      if (isAxiosError(error) && error.response?.status) {
        const status = error.response.status;
        if (
          NO_RETRY_STATUS_CODES.includes(
            status as (typeof NO_RETRY_STATUS_CODES)[number],
          )
        ) {
          return false;
        }
      }
      // Default retry logic: retry up to 3 times for other errors
      return failureCount < 3;
    },
    staleTime: STALE_TIME,
    ...options,
    enabled: !!apiClient && !!caseId && options?.enabled !== false,
  });
};

type UseQueryOptions = UseBaseQueryOptions<
  CaseResponse,
  AxiosError,
  ReturnType<typeof getSerializedData>
>;

export const useCase = (
  caseId: string | undefined | null,
  options?: Omit<UseQueryOptions, "queryKey" | "queryFn">,
) => {
  return useCaseRaw(caseId, {
    select: getSerializedData,
    ...options,
  });
};

type UseCasesParams = {
  environment: GetCasesApiV1CasesGetFilterEnvEnum;
  filters?: Record<string, string>;
  pageSize?: number;
  sort?: string;
};

const deserializeCasesList = (
  params: Pick<CasesResponse, "data" | "included">,
) => deserializeJsonApi<CasesDeserialized>(params).data;

export const useCases = (params: UseCasesParams) => {
  const { wsId, apiClient } = useCasesWorkspaceParams();
  const { environment, filters, pageSize = 100, sort = "-created_at" } = params;

  return useQuery<CasesResponse, AxiosError, CasesDeserialized["data"]>({
    queryKey: casesKeys.list(wsId, environment, filters),
    queryFn: async ({ signal }) => {
      if (!apiClient) {
        throw new Error("API client not available");
      }

      const response = await apiClient.getCasesApiV1CasesGet(
        {
          filterEnv: environment,
          sort,
          pageSize,
        },
        {
          signal,
          params: encodeUrlParams(filters || {}),
        },
      );

      return response.data;
    },
    select: deserializeCasesList,
    staleTime: STALE_TIME,
    enabled: !!apiClient,
  });
};

type UpdateCaseParams = {
  id: string;
  assigneeId?: string | null;
  queueId?: string | null;
  ifMatch?: string | null;
};

type UpdateCaseContext = {
  previousQueueCases?: InfiniteData<CasesResponse>;
  previousCase?: CaseResponse;
  previousCaseQueueSummary?: CaseQueueSummary;
};

export const useUpdateCase = (currentQueueId?: string) => {
  const { wsId, apiClient } = useCasesWorkspaceParams();
  const queryClient = useQueryClient();
  const [environment] = useEnvironment();
  const filters = useURLParamsFilters();
  const userId = useCurrentUserId();

  return useMutation<
    CasePatchResponse,
    AxiosError,
    UpdateCaseParams,
    UpdateCaseContext
  >({
    mutationFn: async (params) => {
      if (!apiClient) {
        throw new Error("API client not available");
      }

      const casePatchRequest: CasePatchRequest = {
        data: {
          type: "cases" as const,
          relationships: {
            ...(params.assigneeId !== undefined && {
              assignee: {
                data: params.assigneeId
                  ? { type: "assignee", id: params.assigneeId }
                  : null,
              },
            }),
            ...(params.queueId !== undefined && {
              queue: {
                data: params.queueId
                  ? { type: "queue", id: params.queueId }
                  : null,
              },
            }),
          },
        },
      };

      const response = await apiClient.patchCaseApiV1CasesIdPatch({
        id: params.id,
        casePatchRequest,
        ifMatch: params.ifMatch,
      });
      return response.data;
    },
    onMutate: (variables) => {
      const previousCaseQueueSummary =
        queryClient.getQueryData<CaseQueueSummary>(
          caseQueuesKeys.summary(wsId, currentQueueId),
        );

      const previousCase = queryClient.getQueryData<CaseResponse>(
        casesKeys.get(wsId, variables.id),
      );

      const previousQueueCases = queryClient.getQueryData<
        InfiniteData<CasesResponse>
      >(caseQueuesKeys.cases(wsId, currentQueueId, environment, filters));

      if (previousCaseQueueSummary) {
        queryClient.setQueryData<CaseQueueSummary>(
          caseQueuesKeys.summary(wsId, currentQueueId),
          (oldSummary) => {
            return produce(oldSummary, (draft) => {
              if (!draft) return;

              const isAssignment = !!variables.assigneeId;
              const isUnassignment = variables.assigneeId === null;

              if (isAssignment) {
                draft.attributes.unassigned -= 1;

                const isAssignedToCurrentUser = variables.assigneeId === userId;
                if (isAssignedToCurrentUser)
                  draft.attributes.assigned_to_user += 1;
              } else if (isUnassignment) {
                draft.attributes.unassigned += 1;

                const previousCaseAssignee =
                  previousCase?.data.relationships?.assignee.data;
                if (previousCaseAssignee?.id === userId) {
                  draft.attributes.assigned_to_user -= 1;
                }
              }
            });
          },
        );
      }

      if (previousQueueCases && variables.assigneeId !== undefined) {
        const updateCase = (page: CasesResponse) => {
          const caseIndex = page.data.findIndex(
            (caseData) => caseData.id === variables.id,
          );
          if (caseIndex === -1) return;

          const data = page.data[caseIndex];
          const hasAssigneeChanged = variables.assigneeId !== undefined;
          if (hasAssigneeChanged && data.relationships) {
            data.relationships.assignee = updateAssignee(variables.assigneeId);
          }

          // Increment etag - API sends strings but type says number
          if (data.attributes?.etag !== undefined) {
            (data.attributes as { etag: string | number }).etag = incrementEtag(
              data.attributes.etag,
            );
          }
        };

        queryClient.setQueryData<InfiniteData<CasesResponse>>(
          caseQueuesKeys.cases(wsId, currentQueueId, environment, filters),
          (oldCases) => {
            return produce(oldCases, (draft) => {
              draft?.pages.forEach(updateCase);
            });
          },
        );
      }

      if (previousCase) {
        queryClient.setQueryData<CaseResponse>(
          casesKeys.get(wsId, variables.id),
          (oldCase) => {
            return produce(oldCase, (draft) => {
              if (!draft?.data) return;

              const hasAssigneeChanged = variables.assigneeId !== undefined;

              if (hasAssigneeChanged && draft.data.relationships) {
                draft.data.relationships.assignee = updateAssignee(
                  variables.assigneeId,
                );
              }

              // Increment etag - API sends strings but type says number
              if (draft.data.attributes?.etag !== undefined) {
                (draft.data.attributes as { etag: string | number }).etag =
                  incrementEtag(draft.data.attributes.etag);
              }
            });
          },
        );
      }

      return { previousQueueCases, previousCase, previousCaseQueueSummary };
    },
    onSuccess: async (_, variables) => {
      await queryClient.invalidateQueries({
        queryKey: casesKeys.get(wsId, variables.id),
      });
      // We do not invalidate the queue cases list because queue cases
      // don't represent the actual state of cases because of eventual consistency.
      // In case of invalidation it will break the optimistic update.
    },
    onError: (_, variables, context) => {
      if (context?.previousQueueCases) {
        queryClient.setQueryData(
          caseQueuesKeys.cases(wsId, currentQueueId, environment, filters),
          context.previousQueueCases,
        );
      }

      if (context?.previousCase) {
        queryClient.setQueryData(
          casesKeys.get(wsId, variables.id),
          context.previousCase,
        );
      }

      if (context?.previousCaseQueueSummary) {
        queryClient.setQueryData(
          caseQueuesKeys.summary(wsId, currentQueueId),
          context.previousCaseQueueSummary,
        );
      }
    },
  });
};

type BulkUpdateContext = {
  previousQueueCases?: InfiniteData<CasesResponse>;
  previousCaseQueueSummary?: CaseQueueSummary;
};

type BulkCaseAssignParams = {
  operation: "assign";
  caseIds: string[];
  assigneeId: Nullable<string>;
  queueId: string;
};

type BulkCaseArchiveParams = {
  caseIds: string[];
  operation: "archive";
  queueId: string;
};

type BulkCaseUpdateParams = BulkCaseArchiveParams | BulkCaseAssignParams;

type BulkUpdateResult = {
  successfulCaseIds: string[];
  failedCaseIds: string[];
};

export const useBulkCaseUpdate = () => {
  const { wsId, apiClient } = useCasesWorkspaceParams();
  const queryClient = useQueryClient();
  const [environment] = useEnvironment();
  const filters = useURLParamsFilters();
  const userId = useCurrentUserId();

  return useMutation<
    BulkUpdateResult,
    AxiosError,
    BulkCaseUpdateParams,
    BulkUpdateContext
  >({
    mutationFn: async ({ queueId, ...params }) => {
      if (!apiClient) {
        throw new Error("API client not available");
      }

      const atomicOperations: CaseBulkAtomicOperationsRequestAtomicOperationsInner[] =
        params.operation === "assign"
          ? params.caseIds.map((caseId) => ({
              op: "update",
              ref: {
                type: "cases",
                id: caseId,
                relationship: "assignee",
              },
              data: params.assigneeId
                ? {
                    type: "assignees",
                    id: params.assigneeId,
                  }
                : null,
            }))
          : params.caseIds.map((caseId) => ({
              op: "update",
              ref: {
                type: "cases",
                id: caseId,
                relationship: "assignee",
              },
              meta: {
                action: "archive",
              },
            }));

      const operationBatches = chunk(atomicOperations, 100);
      const batchCaseIds = chunk(params.caseIds, 100);

      const results = await Promise.allSettled(
        operationBatches.map(async (batch) => {
          const response =
            await apiClient.applyBulkAtomicOperationsApiV1CasesOperationsQueueQueueIdPost(
              {
                queueId: queueId,
                caseBulkAtomicOperationsRequest: {
                  "atomic:operations": batch,
                },
              },
            );
          return response.data;
        }),
      );

      const successfulCaseIds: string[] = [];
      const failedCaseIds: string[] = [];

      results.forEach((result, index) => {
        if (result.status === "fulfilled") {
          successfulCaseIds.push(...batchCaseIds[index]);
        } else {
          failedCaseIds.push(...batchCaseIds[index]);
        }
      });

      return { successfulCaseIds, failedCaseIds };
    },
    onMutate: (variables) => {
      const previousQueueCases = queryClient.getQueryData<
        InfiniteData<CasesResponse>
      >(caseQueuesKeys.cases(wsId, variables.queueId, environment, filters));

      const previousCaseQueueSummary =
        queryClient.getQueryData<CaseQueueSummary>(
          caseQueuesKeys.summary(wsId, variables.queueId),
        );

      if (previousQueueCases && variables.operation === "archive") {
        const removeCases = (
          page: CasesResponse,
          idx: number,
          pages: CasesResponse[],
        ) => {
          pages[idx].data = page.data.filter(
            (caseData) => !variables.caseIds.includes(caseData.id),
          );
        };

        queryClient.setQueryData<InfiniteData<CasesResponse>>(
          caseQueuesKeys.cases(wsId, variables.queueId, environment, filters),
          (oldCases) => {
            return produce(oldCases, (draft) => {
              draft?.pages.forEach(removeCases);
            });
          },
        );
      }

      if (previousQueueCases && variables.operation === "assign") {
        const updateAssignees = (page: CasesResponse) => {
          page.data.forEach((caseData) => {
            const isAffected = variables.caseIds.includes(caseData.id);
            const hasAssigneeChanged = variables.assigneeId !== undefined;

            if (
              isAffected &&
              hasAssigneeChanged &&
              caseData.relationships?.assignee
            ) {
              caseData.relationships.assignee = updateAssignee(
                variables.assigneeId,
              );
            }

            // Increment etag for affected cases
            if (isAffected && caseData.attributes?.etag !== undefined) {
              (caseData.attributes as { etag: string | number }).etag =
                incrementEtag(caseData.attributes.etag);
            }
          });
        };

        queryClient.setQueryData<InfiniteData<CasesResponse>>(
          caseQueuesKeys.cases(wsId, variables.queueId, environment, filters),
          (oldCases) => {
            return produce(oldCases, (draft) => {
              draft?.pages.forEach(updateAssignees);
            });
          },
        );

        const ownedByCurrentUser = previousQueueCases?.pages.reduce(
          (total, page) => {
            total += page.data.filter(
              (caseData) =>
                caseData.relationships?.assignee.data?.id === userId &&
                variables.caseIds.includes(caseData.id),
            ).length;

            return total;
          },
          0,
        );

        if (previousCaseQueueSummary) {
          queryClient.setQueryData<CaseQueueSummary>(
            caseQueuesKeys.summary(wsId, variables.queueId),
            (oldSummary) => {
              return produce(oldSummary, (draft) => {
                if (!draft) return;

                const isAssignment = !!variables.assigneeId;
                const isUnassignment = variables.assigneeId === null;
                const isAssignedToCurrentUser = variables.assigneeId === userId;

                if (isAssignment) {
                  draft.attributes.unassigned -= variables.caseIds.length;
                  if (isAssignedToCurrentUser) {
                    draft.attributes.assigned_to_user +=
                      variables.caseIds.length;
                  }
                } else if (isUnassignment) {
                  draft.attributes.unassigned += variables.caseIds.length;
                  draft.attributes.assigned_to_user -= ownedByCurrentUser;
                }
              });
            },
          );
        }
      }

      return { previousQueueCases, previousCaseQueueSummary };
    },
    onSuccess: (data, variables) => {
      if (data.failedCaseIds.length > 0) {
        toastActions.failure({
          title: "Bulk update partially failed",
          description: `Failed to update ${data.failedCaseIds.length} out of ${
            data.failedCaseIds.length + data.successfulCaseIds.length
          } cases.`,
        });

        queryClient.invalidateQueries({
          queryKey: caseQueuesKeys.cases(
            wsId,
            variables.queueId,
            environment,
            filters,
          ),
        });
        queryClient.invalidateQueries({
          queryKey: caseQueuesKeys.summary(wsId, variables.queueId),
        });
      }
    },
    onError: (_, variables, context) => {
      if (context?.previousQueueCases) {
        queryClient.setQueryData(
          caseQueuesKeys.cases(wsId, variables.queueId, environment, filters),
          context.previousQueueCases,
        );
      }

      if (context?.previousCaseQueueSummary) {
        queryClient.setQueryData(
          caseQueuesKeys.summary(wsId, variables.queueId),
          context.previousCaseQueueSummary,
        );
      }
    },
  });
};

type BlockConfigPatch =
  | RelatedEntitiesBlockConfigPatch
  | RelatedEventsBlockConfigPatch;

type BlockConfigPatchResponse =
  | RelatedEntitiesBlockConfigPatchResponse
  | RelatedEventsBlockConfigPatchResponse;

type BlockConfigPatchAttributes =
  | RelatedEntitiesCaseBlockConfigPatchAttributes
  | RelatedEventsCaseBlockConfigPatchAttributes;

type UpdateBlockConfigParams = {
  caseId: string;
  blockKey: string;
  attributes: BlockConfigPatchAttributes;
};

type UpdateBlockConfigContext = {
  previousCase?: CaseResponse;
};

const updateBlockConfigInCase = (
  draft: CaseResponse | undefined,
  caseId: string,
  blockKey: string,
  attributes: BlockConfigPatchAttributes,
) => {
  if (!draft?.included) return;

  const blockConfigId = `${caseId}#${blockKey}#config`;
  const blockConfig = draft.included.find(
    (item) => item.type === "case_block_configs" && item.id === blockConfigId,
  );

  if (blockConfig?.attributes) {
    Object.assign(blockConfig.attributes, attributes);
  }
};

export const useUpdateBlockConfig = () => {
  const { wsId, apiClient } = useCasesWorkspaceParams();
  const queryClient = useQueryClient();

  return useMutation<
    BlockConfigPatchResponse,
    AxiosError,
    UpdateBlockConfigParams,
    UpdateBlockConfigContext
  >({
    mutationFn: async ({ caseId, blockKey, attributes }) => {
      if (!apiClient) {
        throw new Error("API client not available");
      }

      const blockConfigPatch = {
        data: {
          type: ModelsCaseDataApiModelsRelatedEntitiesBlockConfigPatchDataTypeEnum.CASE_BLOCK_CONFIGS,
          id: blockKey,
          attributes,
        },
      } as BlockConfigPatch;

      const response =
        await apiClient.patchCaseBlockConfigApiV1CasesCaseIdBlocksBlockKeyConfigPatch(
          {
            caseId,
            blockKey,
            payload1: blockConfigPatch as RelatedEventsBlockConfigPatch,
          },
        );

      return response.data;
    },
    onMutate: ({ caseId, blockKey, attributes }) => {
      const previousCase = queryClient.getQueryData<CaseResponse>(
        casesKeys.get(wsId, caseId),
      );

      if (previousCase?.included) {
        queryClient.setQueryData<CaseResponse>(
          casesKeys.get(wsId, caseId),
          (oldCase) =>
            produce(oldCase, (draft) =>
              updateBlockConfigInCase(draft, caseId, blockKey, attributes),
            ),
        );
      }

      return { previousCase };
    },
    onSuccess: (_, { caseId }) => {
      queryClient.invalidateQueries({
        queryKey: casesKeys.get(wsId, caseId),
      });
    },
    onError: (_, { caseId }, context) => {
      if (context?.previousCase) {
        queryClient.setQueryData(
          casesKeys.get(wsId, caseId),
          context.previousCase,
        );
      }
    },
  });
};

const updateAssignee = (assigneeId?: string | null) => ({
  data: assigneeId
    ? {
        type: "assignees" as const,
        id: assigneeId,
      }
    : null,
});

type FileCategoryFileIds = Record<string, string[]>;

export const FILE_OPERATIONS = {
  UPLOAD: "upload",
  MOVE: "move",
  DELETE: "delete",
} as const;

const createFileCategoryOperation = (
  caseId: string,
  categoryKey: string,
  fileIds: string[],
): FileCategoriesAtomicOperation => ({
  op: FileCategoriesAtomicOperationOpEnum.UPDATE,
  data: {
    type: FileCategoriesAtomicResourceObjectTypeEnum.FILE_CATEGORIES,
    id: `${caseId}#${categoryKey}`,
    attributes: {
      file_ids: fileIds,
    },
  },
});

type UploadFileParams = {
  type: typeof FILE_OPERATIONS.UPLOAD;
  caseId: string;
  file: File;
  categoryKey: string;
  env: UploadFileEnvFilesPostEnvEnum;
  source: FileSource;
  currentFileIds: FileCategoryFileIds;
};

type MoveFileParams = {
  type: typeof FILE_OPERATIONS.MOVE;
  caseId: string;
  fileId: string;
  fileName: string;
  fromCategoryKey: string;
  toCategoryKey: string;
  currentFileIds: FileCategoryFileIds;
};

type DeleteFileParams = {
  type: typeof FILE_OPERATIONS.DELETE;
  caseId: string;
  fileId: string;
  fileName: string;
  categoryKey: string;
  currentFileIds: FileCategoryFileIds;
};

type FileCategoryOperationParams =
  | UploadFileParams
  | MoveFileParams
  | DeleteFileParams;

type FileCategoryOperationResult =
  | { fileId: string; categoryResponse: FileCategoriesAtomicResultsResponse }
  | FileCategoriesAtomicResultsResponse;

type FileCategoryOperationContext = {
  previousCase?: CaseResponse;
};

export const useFileCategoryOperation = () => {
  const { wsId, apiClient } = useCasesWorkspaceParams();
  const queryClient = useQueryClient();
  const { onFileSelect, onFileUpload } = useUploadFile();

  return useMutation<
    FileCategoryOperationResult,
    AxiosError,
    FileCategoryOperationParams,
    FileCategoryOperationContext
  >({
    mutationFn: async (params) => {
      if (!apiClient) {
        throw new Error("API client not available");
      }

      const { caseId, currentFileIds, type } = params;

      switch (type) {
        case FILE_OPERATIONS.UPLOAD: {
          const { file, categoryKey, env, source } = params;

          onFileSelect(file, categoryKey);
          const fileId = await onFileUpload(file, categoryKey, env, source);

          if (!fileId) {
            throw new Error("Upload failed");
          }

          const updatedFileIds = [
            ...(currentFileIds[categoryKey] || []),
            fileId,
          ];

          const request: FileCategoriesAtomicOperationsRequest = {
            "atomic:operations": [
              createFileCategoryOperation(caseId, categoryKey, updatedFileIds),
            ],
          };

          const response =
            await apiClient.applyAtomicFileCategoryOperationsApiV1CasesCaseIdFileCategoriesOperationsPost(
              {
                caseId,
                fileCategoriesAtomicOperationsRequest: request,
              },
            );

          return { fileId, categoryResponse: response.data };
        }

        case FILE_OPERATIONS.MOVE: {
          const { fileId, fromCategoryKey, toCategoryKey } = params;

          const operations = [
            createFileCategoryOperation(
              caseId,
              fromCategoryKey,
              (currentFileIds[fromCategoryKey] || []).filter(
                (id) => id !== fileId,
              ),
            ),
            createFileCategoryOperation(caseId, toCategoryKey, [
              ...(currentFileIds[toCategoryKey] || []),
              fileId,
            ]),
          ];

          const request: FileCategoriesAtomicOperationsRequest = {
            "atomic:operations": operations,
          };

          const response =
            await apiClient.applyAtomicFileCategoryOperationsApiV1CasesCaseIdFileCategoriesOperationsPost(
              {
                caseId,
                fileCategoriesAtomicOperationsRequest: request,
              },
            );

          return response.data;
        }

        case FILE_OPERATIONS.DELETE: {
          const { fileId, categoryKey } = params;

          const updatedFileIds = (currentFileIds[categoryKey] || []).filter(
            (id) => id !== fileId,
          );

          const request: FileCategoriesAtomicOperationsRequest = {
            "atomic:operations": [
              createFileCategoryOperation(caseId, categoryKey, updatedFileIds),
            ],
          };

          const response =
            await apiClient.applyAtomicFileCategoryOperationsApiV1CasesCaseIdFileCategoriesOperationsPost(
              {
                caseId,
                fileCategoriesAtomicOperationsRequest: request,
              },
            );

          return response.data;
        }

        default:
          return assertUnreachable(type);
      }
    },
    onMutate: ({ caseId }) => {
      const previousCase = queryClient.getQueryData<CaseResponse>(
        casesKeys.get(wsId, caseId),
      );
      return { previousCase };
    },
    onSuccess: (_, { caseId, type }) => {
      queryClient.invalidateQueries({ queryKey: casesKeys.get(wsId, caseId) });
      const messages = {
        upload: "File uploaded successfully",
        move: "File moved successfully",
        delete: "File deleted successfully",
      };
      toastActions.success({ title: messages[type] });
    },
    onError: (_, params, context) => {
      if (context?.previousCase) {
        queryClient.setQueryData(
          casesKeys.get(wsId, params.caseId),
          context.previousCase,
        );
      }

      const fileName =
        params.type === FILE_OPERATIONS.UPLOAD
          ? params.file.name
          : params.fileName;
      toastActions.failure({
        description: `${capitalize(params.type)} failed for ${fileName}. Please try again.`,
        title: `File ${capitalize(params.type)} Failed`,
      });
    },
  });
};

type SaveCaseActionParams = {
  etag: string | number;
  actionId: string;
  actionFormId: string;
  actionFormValues: CaseActionFormValues;
};

export const useSaveCaseActionForm = (caseId?: string) => {
  const { wsId, apiClient } = useCasesWorkspaceParams();
  const queryClient = useQueryClient();

  return useMutation<
    CaseActionFormPatchResponse,
    AxiosError,
    SaveCaseActionParams
  >({
    mutationFn: async (data) => {
      if (!apiClient || !caseId) {
        throw new Error("API client or case ID is not available");
      }

      const response =
        await apiClient.patchCaseActionFormStateApiV1CasesIdActionActionIdFormStatePatch(
          {
            actionId: extractActionIdParam(data.actionId),
            id: caseId,
            ifMatch: data.etag.toString(),
            caseActionFormPatchRequest: {
              data: {
                type: "case_action_forms" as const,
                id: data.actionFormId,
                attributes: data.actionFormValues as {
                  [key: string]: AttributesValue;
                },
              },
            },
          },
        );

      return response.data;
    },
    onSuccess: () => {
      if (caseId) {
        queryClient.invalidateQueries({
          queryKey: casesKeys.get(wsId, caseId),
        });
      }
    },
  });
};

type SubmitCaseActionParams = {
  actionId: string;
  /**
   * ETag of the case.
   */
  etag: string;
  formEtag?: string | number;
};

export const useSubmitCaseAction = (caseId?: string) => {
  const { wsId, apiClient } = useCasesWorkspaceParams();
  const queryClient = useQueryClient();

  return useMutation<
    RunningActionsResponse,
    AxiosError,
    SubmitCaseActionParams
  >({
    mutationFn: async (data) => {
      if (!apiClient || !caseId) {
        throw new Error("API client or case ID is not available");
      }

      const response =
        await apiClient.runCaseActionApiV1CasesCaseIdActionsActionIdRunPost({
          actionId: data.actionId,
          caseId,
          ifMatch: data.etag.toString(),
          ifMatchForm: data.formEtag ? data.formEtag.toString() : null,
        });

      return response.data;
    },
    onSuccess: () => {
      if (caseId) {
        queryClient.invalidateQueries({
          queryKey: casesKeys.get(wsId, caseId),
        });
      }
    },
  });
};

type DismissCaseActionParams = {
  actionId: string;
  etag: string | number;
};

export const useDismissCaseAction = (caseId?: string) => {
  const { wsId, apiClient } = useCasesWorkspaceParams();
  const queryClient = useQueryClient();

  return useMutation<
    RunningActionsResponse,
    AxiosError,
    DismissCaseActionParams
  >({
    mutationFn: async (data) => {
      if (!apiClient || !caseId) {
        throw new Error("API client or case ID is not available");
      }

      const response =
        await apiClient?.dismissCaseRunningActionApiV1CasesCaseIdRunningActionsRunningActionIdDismissPost(
          {
            runningActionId: data.actionId,
            caseId,
            ifMatch: data.etag.toString(),
          },
        );

      return response?.data;
    },
    onSuccess: () => {
      if (caseId) {
        queryClient.invalidateQueries({
          queryKey: casesKeys.get(wsId, caseId),
        });
      }
    },
  });
};

type RetryCaseActionParams = {
  actionId: string;
  etag: string | number;
};

export const useRetryCaseAction = (caseId?: string) => {
  const { wsId, apiClient } = useCasesWorkspaceParams();
  const queryClient = useQueryClient();

  return useMutation<RunningActionsResponse, AxiosError, RetryCaseActionParams>(
    {
      mutationFn: async (data) => {
        if (!apiClient || !caseId) {
          throw new Error("API client or case ID is not available");
        }

        const response =
          await apiClient.retryCaseRunningActionApiV1CasesCaseIdRunningActionsRunningActionIdRetryPost(
            {
              caseId,
              runningActionId: data.actionId,
              ifMatch: data.etag.toString(),
            },
          );

        return response.data;
      },
      onSuccess: () => {
        if (caseId) {
          queryClient.invalidateQueries({
            queryKey: casesKeys.get(wsId, caseId),
          });
        }
      },
    },
  );
};

/**
 * Extracts action ID param from an action ID.
 * Example: For an action ID "action#param1#param2", it returns "param1#param2".
 */
const extractActionIdParam = (actionId: string) => {
  const [, ...paramPart] = actionId.split("#");
  return paramPart.join("#");
};

// Task-related mutations

type SaveCaseTaskFormParams = {
  taskKey: string;
  fullTaskId: string;
  /**
   * ETag of the form (for form data optimistic locking).
   */
  formEtag: string | number;
  taskFormId: string;
  taskFormValues: Record<string, unknown>;
};

export const useSaveCaseTaskForm = (caseId?: string) => {
  const { wsId, apiClient } = useCasesWorkspaceParams();
  const queryClient = useQueryClient();

  return useMutation<
    CaseTaskFormPatchResponse,
    AxiosError,
    SaveCaseTaskFormParams
  >({
    mutationFn: async (data) => {
      if (!apiClient || !caseId) {
        throw new Error("API client or case ID is not available");
      }

      const response =
        await apiClient.patchCaseTaskFormStateApiV1CasesCaseIdTasksTaskKeyFormDataPatch(
          {
            caseId,
            taskKey: TaskIdExtractor.getStatusAndTaskKey(data.fullTaskId),
            ifMatch: data.formEtag.toString(),
            caseTaskFormPatchRequest: {
              data: {
                type: "case_task_forms" as const,
                id: `${TaskIdExtractor.getCaseId(data.fullTaskId)}#${data.taskFormId}`,
                attributes: data.taskFormValues as {
                  [key: string]: AttributesValue;
                },
              },
            },
          },
        );

      return response.data;
    },
    onSuccess: () => {
      if (caseId) {
        queryClient.invalidateQueries({
          queryKey: casesKeys.get(wsId, caseId),
        });
      }
    },
  });
};

type UpdateCaseTaskCompletionParams = {
  taskKey: string;
  taskId: string;
  isCompleted: boolean;
  /**
   * ETag of the case.
   */
  etag: string | number;
};

export const useUpdateCaseTaskCompletion = (caseId?: string) => {
  const { wsId, apiClient } = useCasesWorkspaceParams();
  const queryClient = useQueryClient();

  return useMutation<
    CaseTaskCompletionPatchResponse,
    AxiosError,
    UpdateCaseTaskCompletionParams
  >({
    mutationFn: async (data) => {
      if (!apiClient || !caseId) {
        throw new Error("API client or case ID is not available");
      }

      const response =
        await apiClient.patchTaskCompletionStatusApiV1CasesCaseIdTasksTaskKeyPatch(
          {
            caseId,
            taskKey: TaskIdExtractor.getStatusAndTaskKey(data.taskId),
            ifMatch: data.etag.toString(),
            caseTaskCompletionPatchRequest: {
              data: {
                type: "case_tasks" as const,
                // Send full task ID: caseid#status#task_key
                id: TaskIdExtractor.getFullId(data.taskId),
                attributes: {
                  is_completed: data.isCompleted,
                },
              },
            },
          },
        );

      return response.data;
    },
    onSuccess: () => {
      if (caseId) {
        queryClient.invalidateQueries({
          queryKey: casesKeys.get(wsId, caseId),
        });
      }
    },
  });
};
